import type { AuditRecordRef } from "../api/client.js";
import type { AuditVerdict, DraftOutcome, Evidence, Health, KycConfirmed } from "../api/types.js";
import { clock, dateTime } from "../lib/format.js";

/**
 * Judge mode (UX spec v3 §6.14, copy §8.3): a different register — dense, mono
 * figures, the API's own field names — built from the bank's answers only:
 * `GET /v1/audit/bundles/{id}/evidence` (said → parsed → shown → signed →
 * executed), `GET /v1/audit/verify` (the chain), `/healthz` (what is running).
 * Nothing here is illustrative: a figure the bank did not return is not drawn.
 * The one thing computed is the re-check — the payload the proof shows, re-hashed
 * in this browser (jcs.ts) and compared with what the bank stored and signed.
 */
export type Part =
  | { readonly kind: "text"; readonly t: string }
  | { readonly kind: "mono"; readonly t: string }
  /** A rule id: mono, and never broken at its hyphens (DCTA-75 P2-8). */
  | { readonly kind: "id"; readonly t: string }
  /** `short`: what the chip shows instead of the hash's ends (DCTA-209: a mandate's "mdt·" + 8 hex). */
  | { readonly kind: "hash"; readonly full: string; readonly label: string; readonly short?: string }
  | { readonly kind: "pass"; readonly t: string }
  | { readonly kind: "fail"; readonly t: string };

export interface JRow {
  readonly k: string;
  readonly v: readonly Part[];
  readonly small?: string;
  /** DCTA-198: the row is the Proof's "Play the clip" for this kept clip (the view fetches it on the tap and re-hashes it). */
  readonly play?: { readonly audioId: string; readonly sha256: string };
}

export interface Stage {
  readonly n: number;
  readonly title: string;
  readonly meta: string;
  /** DCTA-176: the header's verdict in its own ink ("fail" for the identity check's gate, "✓" for a verified signature). */
  readonly tone?: "pass" | "fail";
  readonly rows: readonly JRow[];
  /** Stage 7: the gateway's ordered checks, each with the reason code it would have returned. */
  readonly checks?: readonly { readonly name: string; readonly code: string }[];
}

/** What this browser recomputed from the payload the proof shows. */
export interface Recheck {
  readonly text: string;
  readonly bytes: number;
  readonly canonicalHex: string;
  readonly challenge: string;
}

const t = (s: string): Part => ({ kind: "text", t: s });
const m = (s: string): Part => ({ kind: "mono", t: s });
const h = (full: string, label: string): Part => ({ kind: "hash", full, label });
const ok = (s: string): Part => ({ kind: "pass", t: s });
const bad = (s: string): Part => ({ kind: "fail", t: s });

/**
 * The reason code each check would have returned — read from
 * `lab/dcta/src/gateway/verify.ts` (the names are the ones its
 * `signature_verified` record lists). The proof lists the checks the RECORD
 * names, in the record's order and number; this table only gives each one its
 * code (a name it does not know is listed with none — test/judge.spec.ts reads
 * the gateway's literal so a new check turns a test red until it is named here).
 */
export const CHECK_CODES: Readonly<Record<string, string>> = {
  bundle_owned: "TXN_NOT_FOUND",
  not_expired: "CHALLENGE_EXPIRED",
  credential_registered: "CREDENTIAL_UNKNOWN",
  credential_owned: "CREDENTIAL_NOT_OWNED",
  single_use_claimed: "CHALLENGE_CONSUMED",
  challenge_recomputed_from_stored_payload: "never taken from the client",
  client_data_type: "CLIENT_DATA_TYPE",
  challenge_equal: "CHALLENGE_MISMATCH",
  origin_allowed: "ORIGIN_MISMATCH",
  counter: "COUNTER_REPLAY",
  signature: "SIGNATURE_INVALID",
  user_verified: "UV_REQUIRED",
  // DCTA-99 run 1 inc3 (f84ef90): a /2 summary must be what its own stored steps add up to.
  bundle_summary: "BUNDLE_SUMMARY_MISMATCH",
};

function clientData(b64: string | undefined): Record<string, unknown> {
  if (b64 === undefined) return {};
  try {
    let s = b64.replace(/-/g, "+").replace(/_/g, "/");
    while (s.length % 4 !== 0) s += "=";
    const bytes = Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
  } catch {
    return {};
  }
}

/** DCTA-198: a clip past its 24 h — the bytes are gone, the hash stays on the chain (ADR-0024). */
export const CLIP_DELETED = "deleted after 24 h; the hash stays on the chain";

const AUDIO_WORDS: Readonly<Record<"typed" | "not_sent" | "not_recorded", string>> = {
  typed: "typed — no audio",
  not_sent: "spoken — no clip sent",
  not_recorded: "not recorded",
};

/**
 * DCTA-198 (docs/specs/fe-audio-evidence-dcta191.md Change 2): each turn's audio in the Said stage — a kept clip's hash at
 * upload, its length and size, the way to play it (the view fetches the bytes only on the tap: each read is audited) and the
 * bank's re-hash now; or, in words, typed / spoken with no clip sent / not recorded / deleted. Then the retention, once.
 * An older bank's evidence (no `audio`): the one line it had.
 */
function audioRows(said: Evidence["said"]): JRow[] {
  const turns = said.audio;
  if (turns === undefined) return [{ k: "Audio", v: [t(said.audio_retention ?? "not stored")] }];
  const rows: JRow[] = [];
  turns.forEach((a, i) => {
    const k = `Audio · turn ${String(i + 1)}`;
    if (a.audio !== "stored") {
      rows.push({ k, v: [t(AUDIO_WORDS[a.audio])] });
      return;
    }
    rows.push({ k, v: [t(a.kept === true ? "kept" : CLIP_DELETED)] });
    if (a.audio_sha256 !== undefined) rows.push({ k: "audio_sha256", v: [h(a.audio_sha256, "audio_sha256")] });
    if (a.duration_ms !== undefined && a.bytes !== undefined) {
      rows.push({ k: "Duration · size", v: [t(`${(a.duration_ms / 1000).toFixed(1)} s · ${a.bytes.toLocaleString("en-US")} bytes`)] });
    }
    if (a.kept === true && a.audio_id !== undefined && a.audio_sha256 !== undefined) {
      rows.push({ k: "Play", v: [t("Play the clip")], play: { audioId: a.audio_id, sha256: a.audio_sha256 } });
    }
    rows.push({ k: "The bank's re-hash", v: [a.kept !== true ? t(CLIP_DELETED) : a.rehash_matches === true ? ok("✓ matches") : bad("✗ differs")] });
  });
  if (said.audio_retention !== undefined) rows.push({ k: "Retention", v: [t(said.audio_retention)] });
  return rows;
}

/** Judge mode only: a payload made on one of the bank's own screens (DCTA-123, ADR-0013) — no model ran. */
const NO_MODEL = "none (made on the bank's own screen)";

export function proofModel(e: Evidence, verify: AuditVerdict | undefined, recheck: Recheck | undefined): Stage[] {
  const p = e.shown.payload;
  const decision = e.parsed.policy_decision[0];
  const sig = e.signed.signature[0];
  const verified = e.signed.signature_verified[0];
  const cd = clientData(sig?.client_data_json);
  const displayed = e.shown.displayed[0];
  const stages: Stage[] = [];

  stages.push({
    n: 1,
    title: "Said",
    meta: e.said.transcript_sha256 === undefined ? "" : "transcript",
    rows: [
      { k: "Transcript", v: [t(`“${e.said.transcript}”`)] },
      { k: "transcript_sha256", v: [h(e.said.transcript_sha256, "transcript_sha256")] },
      ...audioRows(e.said),
    ],
  });
  stages.push({
    n: 2,
    title: "Drafted",
    meta: "dcta.draft/1",
    rows: [
      // DCTA-123 (ADR-0013): a bank screen's payload has no words, no draft and no model.
      { k: "Drafter", v: [m(p.drafter?.model_id ?? NO_MODEL)] },
      { k: "prompt_sha256", v: [p.drafter === undefined ? m(NO_MODEL) : h(p.drafter.prompt_sha256, "prompt_sha256")] },
      { k: "Steps", v: [t(p.steps.map((s) => s.type).join(" · "))] },
      { k: "draft_sha256", v: [h(e.said.draft_sha256, "draft_sha256")] },
    ],
  });
  stages.push({
    n: 3,
    title: "Validated",
    meta: p.validator === undefined ? "none" : `verdict ${p.validator.verdict}`,
    rows: [
      { k: "Validator", v: [m(p.validator?.model_id ?? NO_MODEL)], small: "a second reading of the transcript; verdict only" },
      { k: "report_sha256", v: [p.validator === undefined ? m(NO_MODEL) : h(p.validator.report_sha256, "report_sha256")] },
    ],
  });
  stages.push({
    n: 4,
    title: "Decided",
    meta: `policy ${p.policy_version}`,
    rows: (decision?.steps ?? []).map((s) => ({
      k: `s${s.index} ${p.steps.find((x) => x.step_id === s.step_id)?.type.replace(/_.*/, "") ?? ""}`.trim(),
      v: [t(`${s.outcome} · `), ...s.rule_ids.flatMap((id, i): Part[] => (i === 0 ? [{ kind: "id", t: id }] : [t(" · "), { kind: "id", t: id }]))],
      ...(s.warnings.length === 0 ? {} : { small: s.warnings.join(" · ") }),
    })),
  });
  const matches = recheck !== undefined && recheck.canonicalHex === e.shown.canonical_sha256;
  const challengeMatches = recheck !== undefined && recheck.challenge === e.signed.challenge;
  stages.push({
    n: 5,
    title: "Payload",
    meta: p.v,
    rows: [
      {
        k: "JCS",
        v: [t("RFC 8785"), ...(recheck === undefined ? [] : [t(" · "), m(`${recheck.bytes.toLocaleString("en-SG")} bytes`)])],
        small: "canonicalised once by the bank, stored, never re-serialised to be believed",
      },
      { k: "canonical_sha256", v: [h(e.shown.canonical_sha256, "canonical_sha256")] },
      {
        k: "Here",
        v: recheck === undefined ? [t("not recomputed")] : matches ? [ok("✓"), t(" recomputed in this browser from the payload shown")] : [bad("✗"), t(" this browser's digest differs")],
      },
      { k: "Challenge", v: [m("base64url(SHA-256(JCS(payload)))")] },
      { k: "=", v: [h(e.signed.challenge, "challenge"), ...(recheck === undefined ? [] : [t(" "), challengeMatches ? ok("equal") : bad("differs")])] },
      { k: "Expires", v: [m(p.expires_at)], small: `created ${p.created_at} · re-issued, never extended` },
      { k: "Nonce", v: [h(p.nonce, "nonce")] },
      ...(displayed === undefined
        ? []
        : [
            {
              k: "Displayed",
              v: [m(`renderer_assets_version ${displayed.renderer_assets_version ?? "—"}`)],
              small: `warnings shown: ${(displayed.warnings_shown ?? []).join(" · ") || "none"} · interventions shown: ${String((displayed.interventions_shown ?? []).length)}`,
            },
          ]),
    ],
  });
  stages.push({
    n: 6,
    title: "Signed",
    meta: sig?.at === undefined ? "" : clock(sig.at),
    rows:
      sig === undefined
        ? [{ k: "Signature", v: [t("none recorded")] }]
        : [
            { k: "Credential", v: [h(sig.credential_id, "credential_id")] },
            {
              k: "Flags",
              v: [t(`UV ${sig.user_verified === "true" ? "1" : "0"} · BE ${sig.backup_eligible === "true" ? "1" : "0"} · BS ${sig.backed_up === "true" ? "1" : "0"}`)],
              small: sig.backed_up === "true" ? "a synced passkey — backed up, not bound to one device" : "not backed up (this authenticator)",
            },
            { k: "clientData", v: [t("type "), m(String(cd["type"] ?? "—")), t(" · origin "), m(String(cd["origin"] ?? "—"))] },
            {
              k: "Challenge in it",
              v: [h(String(cd["challenge"] ?? ""), "challenge in clientDataJSON"), t(" "), cd["challenge"] === e.signed.challenge ? ok("equal") : bad("differs")],
            },
            { k: "Counter", v: [m(sig.new_counter ?? "—")] },
            { k: "Signature", v: [h(sig.signature, "signature")] },
          ],
  });
  const names = (verified?.checks ?? "").split(",").filter((n) => n !== "");
  stages.push({
    n: 7,
    title: "Verified",
    meta: "gateway · confirm",
    rows: [
      {
        k: "Result",
        v: verified?.result === "pass" ? [ok("pass"), t(` · ${String(names.length)} of ${String(names.length)} · `), m("signature_verified")] : [bad(verified?.result ?? "no record")],
      },
    ],
    checks: names.map((name) => ({ name, code: CHECK_CODES[name] ?? "" })),
  });
  stages.push({
    n: 8,
    title: "Executed",
    meta: `${String(e.executed.steps.length)} ${e.executed.steps.length === 1 ? "step" : "steps"} · index order`,
    rows: [
      ...e.executed.steps.map((s) => ({
        k: `${s.index} · ${s.type.replace(/_.*/, "")}`,
        v: [t(`${s.state}${s.hold_until === undefined ? "" : ` until ${dateTime(s.hold_until)}`}`), ...(s.ledger_ref === undefined ? [] : [t(" · "), m(s.ledger_ref)])],
      })),
      ...e.executed.state_transitions.map((x) => ({ k: "State", v: [m(`${x.from} → ${x.to}`)], small: x.steps })),
    ],
  });
  stages.push({
    n: 9,
    title: "Recorded",
    meta: `${String(e.chain.records)} records · seq ${String(e.chain.first_seq)}–${String(e.chain.last_seq)}`,
    rows: [
      { k: "Chain", v: [m("h_n = SHA-256(h_n−1 ‖ JCS(record_n))")] },
      ...(verify === undefined
        ? []
        : [
            { k: "Verify", v: verify.ok ? [ok("✓"), t(` ${verify.statement}`)] : [bad("✗"), t(` ${verify.statement}`)] },
            ...(verify.head_sha256 === "" ? [] : [{ k: "Head", v: [h(verify.head_sha256, "head_sha256")] }]),
          ]),
    ],
  });
  return stages;
}

/** The chain panel (12 chain-ok / chain-broken), as `GET /v1/audit/verify` answers. */
export function chainRows(v: AuditVerdict): JRow[] {
  const rows: JRow[] = [
    { k: "ok", v: [v.ok ? ok("true") : bad("false")] },
    { k: "records_checked", v: [m(v.records_checked)], small: `records_total ${v.records_total}` },
    { k: "verified_from", v: [m(v.verified_from ?? "—")], small: `started_at_seq ${v.started_at_seq ?? "0"}` },
    {
      k: "checkpoints",
      v: [t(`${v.checkpoints?.total ?? "0"} total · `), (v.checkpoints?.failed.length ?? 0) === 0 ? ok(`${v.checkpoints?.verified ?? "0"} verified`) : bad(`${String(v.checkpoints?.failed.length)} failed`)],
      small: "Ed25519, public key on the response",
    },
  ];
  const b = v.broken_link;
  if (b !== undefined) {
    rows.push(
      { k: "broken_link", v: [t("seq "), m(b.seq), t(" · "), m(b.audit_ref ?? ""), t(` · ${b.type ?? ""}`)], small: `problem: ${b.problem}${b.field === undefined ? "" : ` · field ${b.field}`}` },
      { k: "expected", v: [h(b.expected ?? "", "expected chain_sha256")] },
      { k: "found", v: [h(b.found ?? "", "found chain_sha256")] },
    );
  } else if (v.head_sha256 !== "") {
    rows.push({ k: "head_sha256", v: [h(v.head_sha256, "head_sha256")] });
  }
  rows.push({ k: "statement", v: [t(`“${v.statement}”`)] });
  return rows;
}

/**
 * P1-9 (DCTA-192): this session's speech, as the app runs it — the engine that listens (Tencent's streaming ASR, or the
 * browser's own; `fellBack`: Tencent's stepped aside this session), the voice that answers (Tencent's TTS through the bank's
 * speech route, the silent stand-in, or no audio output) and the Read aloud choice.
 */
export interface SpeechFacts {
  readonly input: "tencent" | "browser";
  readonly fellBack: boolean;
  readonly output: "tencent" | "fake" | "none";
  readonly readAloud: "when_i_speak" | "always" | "never";
}

/** "Speech in: Tencent ASR (streaming) · Speech out: Tencent TTS · reads back when you speak" — built from the facts only. */
export function speechLine(s: SpeechFacts): string {
  const heard = s.input === "browser" ? "the browser's own (?asr=browser)" : `Tencent ASR (streaming)${s.fellBack ? ", then the browser's own this session" : ""}`;
  if (s.readAloud === "never") return `Speech in: ${heard} · Speech out: off`;
  if (s.output === "none") return `Speech in: ${heard} · Speech out: off (no audio output here)`;
  const voice = s.output === "fake" ? "a silent stand-in (?speech=fake)" : "Tencent TTS";
  return `Speech in: ${heard} · Speech out: ${voice} · ${s.readAloud === "always" ? "reads every answer" : "reads back when you speak"}`;
}

/** Models and timing (12 on): what drafted, what checked, what runs — the names as the bank signs them. */
export function modelsRows(e: Evidence | undefined, health: Health | undefined, timing: string | undefined, speech?: SpeechFacts): JRow[] {
  const p = e?.shown.payload;
  return [
    ...(p === undefined
      ? []
      : [
          { k: "Drafter", v: [m(p.drafter?.model_id ?? NO_MODEL)] },
          { k: "Validator", v: [m(p.validator?.model_id ?? NO_MODEL)], small: "reads the transcript only; returns a verdict only" },
        ]),
    // P1-9 (DCTA-192): from this session, never a fixed line (it said "TTS off" while the read-back played).
    ...(speech === undefined ? [] : [{ k: "Speech", v: [t(speechLine(speech))] }]),
    {
      k: "Gateway",
      // DCTA-231 (DCTA-225 A4): the service is "dcta <version>" — never "dcta lab".
      v: [m(`dcta ${health?.version ?? "—"} · policy ${health?.policy_version ?? "—"} · assets ${health?.assets_version ?? "—"}`)],
    },
    ...(timing === undefined ? [] : [{ k: "Timing", v: [m(timing)], small: "as the bank measured it (server stages only)" }]),
  ];
}

/** "a1b2c3d4e5…9f8e7d" — short by default, the full value one tap away (never silently cut). */
export function shortHash(full: string): string {
  return full.length > 18 ? `${full.slice(0, 10)}…${full.slice(-6)}` : full;
}

const STAGE_NAMES: Readonly<Record<string, string>> = {
  // DCTA-111: the model path's clock (POST /v1/utterances `model_timings`); judge mode only.
  drafter_ms: "drafter",
  validator_ms: "validator",
  wall_ms: "drafter ∥ validator",
  resolve_policy_ms: "resolve + policy",
  validate_ms: "validate",
  server_ms: "draft route",
  verify_ms: "verify",
  execute_ms: "execute",
  gateway_ms: "confirm route",
};

/** The bank's own stage timings (ask g), as one line: only what it reported, in its order. */
export function timingText(...sets: readonly (Readonly<Record<string, number>> | undefined)[]): string | undefined {
  const parts = sets
    .flatMap((set) => Object.entries(set ?? {}))
    .filter(([, ms]) => Number.isFinite(ms))
    .map(([k, ms]) => `${STAGE_NAMES[k] ?? k.replace(/_ms$/, "")} ${ms < 10 ? ms.toFixed(1) : String(Math.round(ms))} ms`);
  return parts.length === 0 ? undefined : parts.join(" · ");
}

// ------------------------------------------------------------- DCTA-176 F49: the identity check's Proof

/** One panel of a Proof: its title, the bank's id for it, the rows above its stages, and the stages in order. */
export interface Panel {
  readonly title: string;
  readonly id: string;
  readonly head: readonly JRow[];
  readonly stages: readonly Stage[];
}

/** A turn the identity check refused before resolution, as the chat kept it (the words, the bank's answer, the bank's time). */
export interface RefusedTurnProof {
  readonly said: string;
  readonly outcome: DraftOutcome;
  readonly at: string | undefined;
  readonly customerId: string;
}

/** A refresh that ran: its card, the confirm's answer, the check before it, and what the demo Myinfo said it would share. */
export interface RefreshProof {
  readonly bundleId: string;
  readonly kyc: KycConfirmed;
  readonly fromStatus: string;
  readonly customerId: string;
  readonly myinfo?: { readonly items: readonly string[]; readonly demo: boolean } | undefined;
}

const seqOf = (r: AuditRecordRef | undefined): string => (r === undefined ? "" : `#${r.seq}`);
const chainWord = (ok: boolean | undefined): string => (ok === undefined ? "" : ok ? " · chain ✓" : " · chain ✗");

/**
 * §2.5's first panel: Said → Drafted → Eligibility → Recorded. The records come by position only (`GET /v1/audit/records`):
 * the turn's `rejection` is the first after the draft record the answer named (`audit_ref`). Nothing the bank did not return.
 */
export function turnProof(rt: RefusedTurnProof, records: readonly AuditRecordRef[], chainOk: boolean | undefined): Panel {
  const e = rt.outcome.eligibility;
  const turn = rt.outcome.refused_steps?.find((r) => r.scope === "turn");
  const steps = turn?.step_ids ?? (turn === undefined ? [] : [turn.step_id]);
  const drafted = records.find((r) => r.audit_ref === rt.outcome.audit_ref);
  const rejection = drafted === undefined ? undefined : records.find((r) => r.type === "rejection" && Number(r.seq) > Number(drafted.seq));
  const asked = e?.question_asked === false ? "no question asked" : "a question asked";
  const built = e?.payload_built === false ? "no payload built" : "a payload built";
  const shown = (rt.outcome.refused_steps ?? []).length === 1 ? "one refusal shown" : `${String((rt.outcome.refused_steps ?? []).length)} refusals shown`;
  return {
    title: "Turn refused",
    id: rt.outcome.draft_id,
    head: [
      {
        k: "Customer",
        v: [m(rt.customerId), t(` · KYC ${e?.kyc_status ?? "—"}`)],
        ...(e?.restricted_since === undefined ? {} : { small: `since ${e.restricted_since}` }),
      },
    ],
    stages: [
      { n: 1, title: "Said", meta: rt.at === undefined ? "" : clock(rt.at), rows: [{ k: "Transcript", v: [t(`“${rt.said}”`)] }] },
      {
        n: 2,
        title: "Drafted",
        meta: "dcta.draft/1",
        rows: [{ k: "Steps", v: [m(steps.join(" · ") || "—")], small: "the turn's money steps, refused together · never asked about" }],
      },
      {
        n: 3,
        title: "Eligibility",
        meta: e?.result ?? "",
        ...(e?.result === "fail" ? { tone: "fail" as const } : {}),
        rows: [
          { k: "Rule", v: [{ kind: "id", t: e?.rule_id ?? turn?.rule_ids[0] ?? "—" }], small: `kyc ${e?.kyc_status ?? "—"}` },
          { k: "When", v: [t(e?.before === "resolution" ? "before resolution" : (e?.before ?? "—"))], small: `${asked} · ${built} · ${shown}` },
        ],
      },
      {
        n: 4,
        title: "Recorded",
        meta: seqOf(rejection),
        rows: [{ k: "Record", v: [rejection === undefined ? t("not in this page of records") : m("rejection"), t(`${rejection === undefined ? "" : " · eligibility attached"}${chainWord(chainOk)}`)] }],
      },
    ],
  };
}

/**
 * §2.5's second panel: Consent → Shown → Signed → Verified → Recorded (`kyc_refreshed`). The consent is the customer's own
 * record before the card; the rest is the card's evidence (`/evidence`) and its records by position.
 */
export function refreshProof(r: RefreshProof, e: Evidence, records: readonly AuditRecordRef[], chainOk: boolean | undefined): Panel {
  const decided = records.find((x) => x.bundle_id === r.bundleId && x.type === "policy_decision");
  const consent = [...records].reverse().find((x) => x.type === "consent" && (decided === undefined || Number(x.seq) < Number(decided.seq)));
  const refreshed = records.find((x) => x.bundle_id === r.bundleId && x.type === "kyc_refreshed");
  const sig = e.signed.signature[0];
  const verified = e.signed.signature_verified[0];
  const once = (verified?.checks ?? "").split(",").includes("single_use_claimed");
  const items = r.myinfo?.items ?? [];
  return {
    title: "KYC refresh",
    id: r.bundleId,
    head: [
      { k: "Customer", v: [m(r.customerId)] },
      { k: "KYC", v: [t(`${r.fromStatus} → ${r.kyc.status}`)], small: `next review ${r.kyc.next_review_due} · demo world only` },
    ],
    stages: [
      {
        n: 1,
        title: "Consent",
        meta: consent === undefined ? "" : clock(consent.recorded_at),
        rows: [{ k: "Source", v: [t(`Myinfo stand-in${r.myinfo?.demo === true ? " · demo" : ""} · ${String(items.length)} items (${items.join(", ")})`)] }],
      },
      { n: 2, title: "Shown", meta: e.shown.payload.steps.map((s) => s.type).join(" · "), rows: [{ k: "payload_sha256", v: [h(e.shown.canonical_sha256, "payload_sha256")] }] },
      {
        n: 3,
        title: "Signed",
        meta: sig?.at === undefined ? "" : clock(sig.at),
        rows: [{ k: "Passkey", v: [t(sig === undefined ? "no signature recorded" : sig.user_verified === "true" ? "user verified" : "user not verified")], small: "over the payload above" }],
      },
      {
        n: 4,
        title: "Verified",
        meta: verified?.result === "pass" ? "✓" : "✗",
        tone: verified?.result === "pass" ? "pass" : "fail",
        rows: [{ k: "Gateway", v: [t(verified?.result === "pass" ? `signature valid${once ? " · challenge used once" : ""}` : (verified?.result ?? "no record"))] }],
      },
      {
        n: 5,
        title: "Recorded",
        meta: seqOf(refreshed),
        rows: [{ k: "Record", v: [refreshed === undefined ? t("not in this page of records") : m("kyc_refreshed"), t(chainWord(chainOk))] }],
      },
    ],
  };
}
